import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { usePageTitle } from "../../hooks/usePageTitle";
import { FiAlertTriangle } from "react-icons/fi";
import CheckoutAuthStep from "./CheckoutAuthStep";
import { parseExtensionAuthParams } from "../../utils/extensionAuthParams";
import CheckoutProcessing from "./CheckoutProcessing";
import CheckoutSuccess from "./CheckoutSuccess";
import CheckoutCancel from "./CheckoutCancel";
import cloudApi from "../../services/cloudApi";
import analytics from "../../services/analytics";

const VALID_INTERVALS = ["monthly", "yearly"];

const AnimatedDots = () => (
  <span className="inline-flex w-6 ml-0.5">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="opacity-0 animate-[dotPulse_1.4s_ease-in-out_infinite]"
        style={{ animationDelay: `${i * 0.3}s` }}
      >
        .
      </span>
    ))}
    <style>{`
      @keyframes dotPulse {
        0%, 60%, 100% { opacity: 0; }
        30% { opacity: 1; }
      }
    `}</style>
  </span>
);

const PlanSummaryBadge = ({ planData, interval }) => {
  if (!planData) return null;
  const price = interval === "yearly" ? (planData.yearly_price ?? planData.price) : planData.price;
  const period = interval === "yearly" ? "yr" : "mo";

  return (
    <div className="w-full max-w-md mx-auto mb-6">
      <div className="flex items-center justify-between px-5 py-3.5 rounded-2xl bg-white/60 dark:bg-zinc-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-zinc-700/50 shadow-sm">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-zinc-100">
          Payment in progress<AnimatedDots />
        </h2>
        <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-purple-500/10 dark:from-blue-500/15 dark:via-cyan-500/15 dark:to-purple-500/15 border border-cyan-400/20 dark:border-cyan-400/15">
          <span className="text-sm font-bold text-gray-800 dark:text-zinc-200">
            {planData.display_name}
          </span>
          <span className="text-sm font-bold bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent">
            ${price}/{period}
          </span>
        </div>
      </div>
    </div>
  );
};

const CheckoutWizard = () => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading } = useAuth();

  const plan = searchParams.get("plan");
  const interval = searchParams.get("interval") || "monthly";
  const catalog = searchParams.get("catalog") || undefined;
  const checkoutStatus = searchParams.get("checkout");
  const source = searchParams.get("source") || "web";
  const extensionAuthOptions = useMemo(() => parseExtensionAuthParams(searchParams), [searchParams]);
  const isExtensionAuthFlow = Boolean(extensionAuthOptions);

  usePageTitle("Checkout");

  const [plans, setPlans] = useState(null);
  const [plansError, setPlansError] = useState(null);
  const [extensionLinkingStatus, setExtensionLinkingStatus] = useState(
    isExtensionAuthFlow ? "pending" : "done"
  );

  useEffect(() => {
    let cancelled = false;
    cloudApi
      .getAvailablePlans(catalog)
      .then((data) => {
        if (!cancelled) setPlans(data?.plans || []);
      })
      .catch((err) => {
        if (!cancelled) setPlansError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [catalog]);

  // Already-authenticated extension users: hand off the session cookie the
  // extension polls for, since we're skipping CheckoutAuthStep (which normally
  // sets it during login).
  useEffect(() => {
    if (loading || !isAuthenticated || !isExtensionAuthFlow) return;
    if (extensionLinkingStatus !== "pending") return;

    let cancelled = false;
    cloudApi
      .maybePrepareExtensionSessionCookie(extensionAuthOptions)
      .then(() => {
        if (!cancelled) setExtensionLinkingStatus("done");
      })
      .catch(() => {
        if (!cancelled) setExtensionLinkingStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [loading, isAuthenticated, isExtensionAuthFlow, extensionAuthOptions, extensionLinkingStatus]);

  const planData = plans?.find((p) => p.name === plan) || null;
  const isValidPlan = !!planData;
  const isValidInterval = VALID_INTERVALS.includes(interval);
  const plansLoading = plans === null && !plansError;

  // Top of the wizard funnel: fire once the auth state and plans have resolved,
  // recording whether the user arrived already signed in. Skipped on the Stripe
  // success/cancel return views — those aren't a fresh checkout entry.
  const wizardViewedRef = useRef(false);
  useEffect(() => {
    if (wizardViewedRef.current || loading || plansLoading || checkoutStatus) return;
    wizardViewedRef.current = true;
    analytics.trackCheckoutWizardViewed({
      plan: planData,
      interval,
      source,
      flow: "wizard",
      authenticated: isAuthenticated,
    });
  }, [loading, plansLoading, checkoutStatus, planData, interval, source, isAuthenticated]);

  // Determine which step to render
  const renderStep = () => {
    // Stripe return states
    if (checkoutStatus === "success") {
      return <CheckoutSuccess plan={planData} interval={interval} source={source} />;
    }
    if (checkoutStatus === "cancel") {
      return <CheckoutCancel plan={plan} interval={interval} />;
    }

    // Wait for plans to load before validating plan name
    if (plansLoading) {
      return (
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 p-8 flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 dark:border-cyan-400"></div>
            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-4">Loading...</p>
          </div>
        </div>
      );
    }

    // Invalid params
    if (!isValidPlan || !isValidInterval) {
      return (
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mb-5">
              <FiAlertTriangle className="w-7 h-7 text-yellow-500 dark:text-yellow-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-zinc-100 mb-2">
              Invalid checkout link
            </h2>
            <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed">
              This checkout link appears to be invalid or incomplete. Please try again from the extension.
            </p>
          </div>
        </div>
      );
    }

    // Auth loading - wait before deciding
    if (loading) {
      return (
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 p-8 flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 dark:border-cyan-400"></div>
            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-4">Loading...</p>
          </div>
        </div>
      );
    }

    // Not authenticated - show sign-in
    if (!isAuthenticated) {
      return <CheckoutAuthStep plan={plan} interval={interval} extensionAuthOptions={extensionAuthOptions} />;
    }

    // Extension handoff still in progress - wait before creating checkout session
    if (extensionLinkingStatus === "pending") {
      return (
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 p-8 flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 dark:border-cyan-400"></div>
            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-4">Linking extension...</p>
          </div>
        </div>
      );
    }

    // Authenticated - create checkout session
    return <CheckoutProcessing plan={plan} interval={interval} source={source} planData={planData} />;
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-black dark:via-gray-950 dark:to-black text-gray-900 dark:text-white">
      {/* Ambient background gradients */}
      <div
        className="absolute -left-24 -top-24 w-80 h-80 sm:w-96 sm:h-96 bg-blue-500/5 dark:bg-blue-500/10 blur-3xl rounded-full animate-pulse"
        aria-hidden="true"
        style={{ animationDuration: "8s" }}
      />
      <div
        className="absolute -right-24 -bottom-24 w-80 h-80 sm:w-96 sm:h-96 bg-cyan-500/5 dark:bg-cyan-500/10 blur-3xl rounded-full animate-pulse"
        aria-hidden="true"
        style={{ animationDuration: "10s", animationDelay: "1s" }}
      />

      <div className="relative min-h-screen flex flex-col items-center justify-center p-4 sm:p-6">
        {/* Plan badge - shown for valid plans outside of success/cancel */}
        {isValidPlan && !checkoutStatus && <PlanSummaryBadge planData={planData} interval={interval} />}

        {renderStep()}

        <p className="text-[10px] text-gray-400 dark:text-zinc-600 text-center mt-6">
          Ultimate Web Scraper
        </p>
      </div>
    </div>
  );
};

export default CheckoutWizard;
